import { useEffect, useState } from "react";
import { StatusPill } from "../../../design/components";
import fieldTitle from "./fieldTitle";
import styles from "./mapping.module.css";
import { SourcePicker } from "./SourcePicker";
import { TransformPicker } from "./TransformPicker";
import {
  MODES,
  rowStatus,
  type MappingEntryDto,
  type RowDto,
  type RowMeta,
  type TransformDto,
} from "./types";

const TAG_CLASS: Record<string, string | undefined> = {
  required: styles.tagRequired,
  conditional: styles.tagConditional,
  optional: styles.tagOptional,
};

function TagChip({ requirement }: { requirement: string }) {
  return <span className={`${styles.tag} ${TAG_CLASS[requirement] ?? styles.tagOptional}`}>{requirement}</span>;
}

export type RowChange = (ota: string, entry: MappingEntryDto) => void;

export function Row({
  row,
  entityId,
  meta,
  transforms,
  preview,
  failure,
  readonly = false,
  onChange,
}: {
  row: RowDto;
  entityId: string | undefined;
  meta?: RowMeta | undefined;
  transforms?: TransformDto[] | undefined;
  preview?: string | undefined;
  failure?: string | undefined;
  readonly?: boolean | undefined;
  onChange: RowChange;
}) {
  // Business Term + ibt is what the user recognises; `ota` stays the key we send
  // back and the anchor id the gap jump-list targets.
  const title = fieldTitle({ label: meta?.label ?? row.title, ibt: meta?.ibt, ota: row.ota });
  const [mode, setMode] = useState<string>(row.mode ?? "unmapped");

  // A refetch or a discarded draft is the server's truth again.
  useEffect(() => setMode(row.mode ?? "unmapped"), [row.mode]);

  const caption = (
    <>
      {title} <TagChip requirement={row.requirement} />
      {meta?.ublPath && <div className={styles.ublPath}>{meta.ublPath}</div>}
      {meta?.note && <div className={styles.note}>{meta.note}</div>}
    </>
  );

  const shown = preview ?? row.preview;

  if (row.derived) {
    return (
      <tr className={styles.rowDerived}>
        <td id={`row-${row.ota}`}>{caption}</td>
        <td colSpan={2} className={styles.note}>
          computed by the gateway from mapped values
        </td>
        <td className={styles.preview}>{shown}</td>
        <td>
          <StatusPill status="derived" label="derived" />
        </td>
      </tr>
    );
  }

  const submit = (patch: Partial<MappingEntryDto> & { mode: string }) => {
    onChange(row.ota, {
      source: row.source,
      value: row.value,
      transform: row.transform,
      args: row.args,
      ...patch,
    });
  };

  return (
    <tr>
      <td id={`row-${row.ota}`}>
        {caption}
        {row.issues.map((issue) => (
          <div key={issue.message} className={styles.error}>
            {issue.message}
          </div>
        ))}
        {failure && <div className={styles.error}>{failure}</div>}
      </td>
      <td>
        <select
          aria-label={`Mode for ${title}`}
          value={mode}
          disabled={readonly}
          onChange={(ev) => {
            setMode(ev.target.value);
            submit({ mode: ev.target.value });
          }}
        >
          {MODES.map((m) => (
            <option key={m} disabled={m === "blank" && row.requirement === "required"}>
              {m}
            </option>
          ))}
        </select>
      </td>
      <td>
        {mode === "field" && (
          <SourcePicker
            id={`src-${row.ota}`}
            label={`Source for ${title}`}
            value={row.source ?? ""}
            level={meta?.level ?? "header"}
            entityId={entityId}
            disabled={readonly}
            onPick={(source) => submit({ mode, source })}
          />
        )}
        {mode === "constant" &&
          (meta?.codeList ? (
            <select
              key={`val-${row.value ?? ""}`}
              defaultValue={row.value ?? ""}
              aria-label={`Value for ${title}`}
              disabled={readonly}
              onChange={(ev) => submit({ mode, value: ev.target.value })}
            >
              <option value="" disabled>
                choose…
              </option>
              {meta.codeList.map((code) => (
                <option key={code}>{code}</option>
              ))}
            </select>
          ) : (
            <input
              key={`val-${row.value ?? ""}`}
              defaultValue={row.value ?? ""}
              aria-label={`Value for ${title}`}
              disabled={readonly}
              onBlur={(ev) => submit({ mode, value: ev.target.value })}
            />
          ))}
        {mode === "computed" && (
          <TransformPicker
            id={`tr-${row.ota}`}
            label={`Transform for ${title}`}
            transform={row.transform ?? ""}
            args={row.args ?? {}}
            transforms={transforms ?? []}
            disabled={readonly}
            onChange={(change) => submit({ mode, ...change })}
          />
        )}
      </td>
      <td className={styles.preview}>{shown}</td>
      <td>
        <StatusPill status={rowStatus(row)} label={rowStatus(row)} />
      </td>
    </tr>
  );
}
